import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StatusPill, { CASE_STATUS_PILL } from '../components/StatusPill';
import ComplianceRing from '../components/ComplianceRing';
import {
  ArrowRight,
  Bubble,
  Doc,
  Folder,
  Padlock,
  Person,
  Scales,
  Send,
  Shield,
  Sparkle,
  Tick,
} from '../components/AppIcons';
import { CATEGORY_META, SUGGESTED_PROMPTS } from '../data';
import { useActiveCompany } from '../state';
import { useCurrentUser } from '../CurrentUserProvider';
import { Brand } from '../../components/Brand';
import { CATEGORY_TO_KEBAB, type ChecklistCategory, type ChecklistItem } from '../../lib/entities/checklist';
import type { ActivityItem } from '../types';
import '../../styles/app-dashboard.css';

const ACTIVITY_ICON: Record<ActivityItem['icon'], ReactNode> = {
  doc: <Doc size={15} />,
  law: <Scales size={15} />,
  case: <Folder size={15} />,
  chat: <Bubble size={15} />,
  expert: <Person size={15} />,
  person: <Person size={15} />,
  shield: <Shield size={15} />,
};

/**
 * Attention ordering: legally required first, then by falling severity, with
 * "should not have" last. Mirrors the backend's category severity so the
 * dashboard's short list agrees with the checklist page.
 */
const ATTENTION_RANK: Record<ChecklistCategory, number> = {
  legallyRequired: 0,
  essential: 1,
  bestPractice: 2,
  optional: 3,
  shouldNotHave: 4,
  notRelevant: 5,
};

/** Severity rank, tolerant of a null (needsAnswer) category — those sort last. */
const rank = (category: ChecklistCategory | null): number =>
  category ? ATTENTION_RANK[category] : 99;

/** The one-line reason a checklist item is on the attention list. */
function concernFor(item: ChecklistItem): string {
  if (item.status === 'shouldNotHave') return 'You shouldn’t hold this. Remove or replace it.';
  if (!item.category) return 'Answer a couple of questions so reeve can place this.';
  return `${CATEGORY_META[CATEGORY_TO_KEBAB[item.category]].label}, not in place yet.`;
}

function attentionPill(item: ChecklistItem): { variant: 'warn' | 'danger'; label: string } {
  if (item.status === 'shouldNotHave') {
    return { variant: 'danger', label: CATEGORY_META['should-not-have'].label };
  }
  return { variant: item.category === 'legallyRequired' ? 'danger' : 'warn', label: 'Missing' };
}

function Meter({ label, ok, total }: { label: string; ok: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((ok / total) * 100);
  return (
    <div className="dash-meter">
      <div className="dash-meter__row">
        <span className="dash-meter__label">{label}</span>
        <span className="dash-meter__count">
          {ok} of {total}
        </span>
      </div>
      <div className="dash-meter__bar">
        <span className="dash-meter__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { company, checklist, cases, activity, loading, companyLoading } = useActiveCompany();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');

  const ask = (text: string) => {
    const q = text.trim();
    if (q) navigate(`/app/assistant?q=${encodeURIComponent(q)}`);
  };
  const onAskSubmit = (e: FormEvent) => {
    e.preventDefault();
    ask(question);
  };

  // The dashboard is built around one active company. Until the portfolio and
  // that company's data have loaded, show a simple placeholder rather than
  // dereferencing a null company or flashing zeroes.
  if (!company) {
    const busy = loading || companyLoading;
    return (
      <div className="apage">
        <PageHeader eyebrow="Overview" title="Dashboard" lead="How can we help?" />
        <div className="acard">
          <div className="empty">
            {busy ? 'Loading your account…' : 'Add a company to see your dashboard.'}
          </div>
        </div>
      </div>
    );
  }

  // Everything on this page's compliance cards reads the live, backend-computed
  // checklist (documents + attestations), so it agrees with the checklist page
  // and moves the moment the company's data changes. Until it loads, the cards
  // show a checking/loading state rather than a misleading "all clear".
  const summary = checklist?.summary ?? null;
  const items = checklist ? [...checklist.documents, ...checklist.attestations] : [];
  // The same weighted score the requirements page headlines, straight off the backend. Computing it
  // here from present/needed would give this ring a DIFFERENT number from the page it links to, for
  // the same company at the same moment.
  const compliance = summary?.score.percent ?? 0;

  const requiredItems = items.filter((i) => i.category === 'legallyRequired' && i.needed);
  const supportingItems = items.filter((i) => i.needed && i.category !== 'legallyRequired');
  const requiredOk = requiredItems.filter((i) => i.status === 'present').length;
  const supportingOk = supportingItems.filter((i) => i.status === 'present').length;

  const attentionItems = items
    .filter((i) => i.status === 'missing' || i.status === 'shouldNotHave')
    .sort((a, b) => rank(a.category) - rank(b.category));
  const attentionCount = attentionItems.length;
  const hasDanger = attentionItems.some(
    (i) => i.category === 'legallyRequired' || i.status === 'shouldNotHave',
  );

  const openCases = cases.filter((c) => c.status !== 'resolved');

  return (
    <div className="apage">
      <PageHeader
        eyebrow="Overview"
        title={<>Good morning, {user?.firstName ?? 'there'}.</>}
        lead="How can we help?"
      />

      <div className="dash-grid">
        {/* Ask reeve — the hero */}
        <section className="dash-ask" aria-label="Ask reeve">
          <div className="dash-ask__head">
            <Sparkle size={15} />
            Your HR adviser
          </div>
          <h2 className="dash-ask__title">
            Ask <Brand /> anything
          </h2>
          <p className="dash-ask__sub">
            Plain-English answers in seconds, grounded in your documents and current UK employment
            law.
          </p>
          <form className="dash-ask__form" onSubmit={onAskSubmit}>
            <input
              type="text"
              className="dash-ask__input"
              placeholder="Ask about hiring, holiday, sickness, tricky conversations…"
              aria-label="Ask reeve a question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
            <button type="submit" className="dash-ask__send" aria-label="Send question">
              <Send size={19} />
            </button>
          </form>
          <div className="dash-ask__chips">
            {SUGGESTED_PROMPTS.slice(0, 4).map((prompt) => (
              <button
                key={prompt}
                type="button"
                className="dash-ask__chip"
                onClick={() => ask(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
          <p className="dash-ask__privacy">
            <Padlock size={13} />
            Private. Conversations expire by default.
          </p>
        </section>

        {/* Where you stand */}
        <section className="acard dash-card dash-status" aria-label="Compliance status">
          <header className="dash-card__head">
            <div>
              <h2 className="acard__title">Where you stand</h2>
              <p className="acard__sub">
                {summary
                  ? `${summary.present} of ${summary.needed} in place`
                  : 'Checking your requirements…'}
              </p>
            </div>
          </header>
          <div className="dash-status__body">
            <ComplianceRing value={compliance} size={116} stroke={10} />
            <div className="dash-status__meters">
              <Meter label="Legally required" ok={requiredOk} total={requiredItems.length} />
              <Meter label="Supporting documents" ok={supportingOk} total={supportingItems.length} />
            </div>
          </div>
          <Link to="/app/documents" className="dash-card__link">
            See your requirements <ArrowRight size={14} />
          </Link>
        </section>

        {/* Needs your attention */}
        <section className="acard dash-card dash-attention" aria-label="Needs your attention">
          <header className="dash-card__head">
            <div>
              <h2 className="acard__title">Needs your attention</h2>
              {attentionCount > 0 && (
                <p className="acard__sub">The short list. Everything else is in order.</p>
              )}
            </div>
            {attentionCount > 0 && (
              <StatusPill variant={hasDanger ? 'danger' : 'warn'}>
                {attentionCount} to review
              </StatusPill>
            )}
          </header>

          {!checklist ? (
            <div className="empty">Checking your requirements…</div>
          ) : attentionCount === 0 ? (
            <div className="dash-clear">
              <span className="dash-clear__mark">
                <Tick size={17} color="#fff" />
              </span>
              <span className="dash-clear__title">All caught up</span>
              <p className="dash-clear__sub">
                Nothing needs your attention today. <Brand /> keeps checking, and will nudge you the
                moment that changes.
              </p>
            </div>
          ) : (
            <div className="rowlist">
              {attentionItems.map((item) => {
                const pill = attentionPill(item);
                const danger = item.status === 'shouldNotHave' || item.category === 'legallyRequired';
                return (
                  <Link key={item.id} to="/app/documents" className="rowlist__row dash-att__row">
                    <span
                      className={`dash-mark ${danger ? 'dash-mark--danger' : 'dash-mark--warn'}`}
                      aria-hidden="true"
                    >
                      !
                    </span>
                    <span className="dash-att__copy">
                      <span className="dash-att__name">{item.name}</span>
                      <span className="dash-att__why">{concernFor(item)}</span>
                    </span>
                    <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* Open cases */}
        <section className="acard dash-card dash-cases" aria-label="Open cases">
          <header className="dash-card__head">
            <div>
              <h2 className="acard__title">Open cases</h2>
              <p className="acard__sub">Each one has a clear next step.</p>
            </div>
            {openCases.length > 0 && (
              <StatusPill variant="indigo" dot={false}>
                {openCases.length}
              </StatusPill>
            )}
          </header>
          {openCases.length === 0 ? (
            <div className="empty">No open cases. Nice and quiet.</div>
          ) : (
            <div className="rowlist">
              {openCases.map((c) => {
                const pill = CASE_STATUS_PILL[c.status];
                return (
                  <Link key={c.id} to={`/app/cases/${c.id}`} className="rowlist__row dash-case__row">
                    <span className="dash-case__head">
                      <span className="dash-case__title">{c.title}</span>
                      <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
                    </span>
                    <span className="dash-case__kind">{c.kind}</span>
                    {c.nextStep && <span className="dash-case__next">{c.nextStep}</span>}
                  </Link>
                );
              })}
            </div>
          )}
          <Link to="/app/cases" className="dash-card__link">
            All cases <ArrowRight size={14} />
          </Link>
        </section>

        {/* Recent activity */}
        <section className="acard dash-card dash-activity" aria-label="Recent activity">
          <header className="dash-card__head">
            <div>
              <h2 className="acard__title">Recent activity</h2>
              <p className="acard__sub">What’s happened across your account.</p>
            </div>
          </header>
          {activity.length === 0 ? (
            <div className="empty">
              Nothing yet. Uploads, confirmations and case work will show up here.
            </div>
          ) : (
            <div className="rowlist">
              {activity.map((a) => (
                <div key={a.id} className="rowlist__row dash-act__row">
                  <span className={`dash-act__icon dash-act__icon--${a.icon}`}>
                    {ACTIVITY_ICON[a.icon]}
                  </span>
                  <span className="dash-act__text">{a.text}</span>
                  <span className="dash-act__when">{a.when}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
